import React, { useRef, useState, useEffect } from 'react';
import './mfa-verification.scss';

const MFA_CODE_LENGTH = 6;

// Define key codes for keyboard events
const KeyCodes = {
  BACKSPACE: 8,
  LEFT_ARROW: 37,
  RIGHT_ARROW: 39,
  ENTER: 13
};

interface Props {
  onVerify: (code: string) => void;
  isVerifying: boolean;
  error: boolean;
  errorMessage?: string;
  success?: boolean;
}

const MfaVerification: React.FC<Props> = ({ onVerify, isVerifying, error, errorMessage, success = false }) => {
  const [mfaCode, setMfaCode] = useState<string[]>(Array(MFA_CODE_LENGTH).fill(''));
  const inputRefs = useRef<(HTMLInputElement | null)[]>(Array(MFA_CODE_LENGTH).fill(null));

  useEffect(() => {
    // Focus the first input when component mounts
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  const handleMfaCodeCharKeyDown = (event: React.KeyboardEvent, index: number) => {
    // Handle backspace
    if (event.keyCode === KeyCodes.BACKSPACE) {
      if (!mfaCode[index] && index > 0) {
        // If current input is empty and not the first one, focus previous input
        inputRefs.current[index - 1]?.focus();
      }
      return;
    }

    // Handle arrow keys
    if (event.keyCode === KeyCodes.LEFT_ARROW && index > 0) {
      inputRefs.current[index - 1]?.focus();
      event.preventDefault();
    } else if (event.keyCode === KeyCodes.RIGHT_ARROW && index < MFA_CODE_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
      event.preventDefault();
    }

    // Handle enter key
    if (event.keyCode === KeyCodes.ENTER) {
      if (mfaCode.every(char => char)) {
        onVerify(mfaCode.join(''));
      }
      event.preventDefault();
    }
  };

  const setMfaCodeChar = (value: string, index: number) => {
    // Only allow single digit
    const digit = value.replace(/\D/g, '').slice(-1);
    
    const newMfaCode = [...mfaCode];
    newMfaCode[index] = digit;
    setMfaCode(newMfaCode);

    // If we have a digit and not the last input, focus next input
    if (digit && index < MFA_CODE_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }

    // If all digits are filled, trigger verification
    if (digit && index === MFA_CODE_LENGTH - 1 && newMfaCode.every(char => char)) {
      onVerify(newMfaCode.join(''));
    }
  };

  const mfaInputPasteHandler = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const pastedData = event.clipboardData.getData('text/plain').replace(/\D/g, '').slice(0, MFA_CODE_LENGTH);
    
    if (!pastedData) return;
    
    const newMfaCode = [...mfaCode];
    
    for (let i = 0; i < pastedData.length; i++) {
      if (i < MFA_CODE_LENGTH) {
        newMfaCode[i] = pastedData[i];
      }
    }
    
    setMfaCode(newMfaCode);
    
    // Focus the next empty input or the last one if all filled
    const nextEmptyIndex = newMfaCode.findIndex(char => !char);
    const focusIndex = nextEmptyIndex === -1 ? MFA_CODE_LENGTH - 1 : nextEmptyIndex;
    inputRefs.current[focusIndex]?.focus();
    
    // If all digits are filled, trigger verification
    if (newMfaCode.every(char => char)) {
      onVerify(newMfaCode.join(''));
    }
  };

  const handleVerifyClick = () => {
    if (mfaCode.every(char => char)) {
      onVerify(mfaCode.join(''));
    }
  };

  return (
    <div className="mfa-verification">
      <div className="mfa-title">Enter verification code</div>
      <div className="mfa-subtitle">Enter the 6-digit code from your authenticator app</div>
      
      <div className="mfa-code-row">
        {Array(MFA_CODE_LENGTH).fill(0).map((_, index) => (
          <input
            key={index.toString()}
            type="text"
            inputMode="numeric"
            pattern="\d*"
            className={`mfa-code-input ${
              error ? 'mfa-code-input-error' : success ? 'mfa-code-input-success' : ''
            }`}
            ref={el => inputRefs.current[index] = el}
            value={mfaCode[index]}
            onKeyDown={event => handleMfaCodeCharKeyDown(event, index)}
            onChange={event => setMfaCodeChar(event.target.value, index)}
            onPaste={mfaInputPasteHandler}
            disabled={isVerifying}
            autoComplete="one-time-code"
          />
        ))}
      </div>
      
      {error && errorMessage && (
        <div className="mfa-error-message">
          {errorMessage}
        </div>
      )}

      <div className="mfa-button-container">
        <button 
          className="mfa-verify-button" 
          onClick={handleVerifyClick}
          disabled={!mfaCode.every(char => char) || isVerifying}
        >
          {isVerifying ? 'Verifying...' : 'Verify'}
        </button>
      </div>
    </div>
  );
};

export default MfaVerification;
